import { useDispatch } from 'react-redux';

import { setShoppablePostCreationSource } from 'actions/influencer/influencer';
import { HEART_DEFAULT_LIST_ID } from 'constants/apis';
import NewCollectionModal from 'components/account/Collections/NewCollectionModal';
import InfluencerCollectionListCard from 'components/influencer/collections/InfluencerCollectionListCard';
import DeleteCollectionModal from 'components/account/Collections/DeleteCollectionModal';
import ShareCollectionModal from 'components/account/Collections/ShareCollectionModal';
import useEffectOnce from 'hooks/useEffectOnce';

import css from 'styles/components/influencer/collections/influencerCollections.scss';

export const InfluencerCollections = ({
  testId,
  currentlyFocusedListId,
  collectionWithItems,
  collectionNextPageToken,
  showNewCollectionModal,
  onCancelNewCollection,
  onSaveNewCollection,
  showDeleteCollectionModal,
  showShareCollectionModal,
  onShareCollection,
  onDeleteCollection,
  onStartNewCollection,
  loadMore,
  onOpenShareModal,
  onOpenDeleteModal
}) => {
  const newCollectionContent = {
    heading: 'Create New Collection',
    label: 'Collection Name',
    hydraLabel: 'Name your Collection',
    markInfluencer: true
  };
  const dispatch = useDispatch();

  useEffectOnce(() => {
    dispatch(setShoppablePostCreationSource('ManageContent'));
  });

  const deleteCollectionContent = {
    heading: 'Delete Collection',
    message: 'Are you sure you want to delete this Collection?'
  };

  let isEmptyInfluencerCollection = true;
  return (
    <div>
      <NewCollectionModal
        showNewCollectionModal={showNewCollectionModal}
        onCancelModal={onCancelNewCollection}
        onDoneModal={onSaveNewCollection}
        content={newCollectionContent}
      />
      <DeleteCollectionModal
        showDeleteCollectionModal={showDeleteCollectionModal}
        listId={currentlyFocusedListId}
        onDoneModal={onDeleteCollection}
        content={deleteCollectionContent}
      />
      <ShareCollectionModal
        showShareCollectionModal={showShareCollectionModal}
        listId={currentlyFocusedListId}
        isInfluencerCollection={true}
        onDoneModal={onShareCollection}
      />
      <div className={css.headingContainer}>
        <section className={css.accountHeader}>
          <h2>Collections</h2>
        </section>
        <button className={css.newCollection} type="button" onClick={onStartNewCollection} data-test-id={testId('newCollectionButton')}>
          Create New Collection
        </button>
      </div>
      <p className={css.headingTitle}>Add products to collections and make it easy for your followers to find items you love!</p>
      <section className={css.collectionListContainer} key={`${collectionWithItems.length}-collectionContainer`}>
        {collectionWithItems?.map(collection => {
          const { listId, shareToken, hearts, name, metadata = {} } = collection;
          const { subCopy, headerLayout, images, public: isPublic } = metadata;

          if (listId !== HEART_DEFAULT_LIST_ID && isPublic) {
            isEmptyInfluencerCollection = false;
            const imageObject = Object.assign(
              {},
              ...images.map(({ type, approved, feedback }) => ({
                [type]: {
                  approved,
                  feedback
                }
              }))
            );
            const selectedImageObject = headerLayout === 2 ? imageObject['squareHero'] : imageObject['rectangleHero'];
            const { approved, feedback } = selectedImageObject || {};
            let declined = false,
              pendingReview = false,
              readyToShare = false,
              shared = false;

            if (headerLayout !== 2 || !selectedImageObject) {
              // No image layout conditions
              shareToken ? (shared = true) : (readyToShare = true);
            } else {
              if (approved === false) {
                feedback ? (declined = true) : (pendingReview = true);
              } else {
                shareToken ? (shared = true) : (readyToShare = true);
              }
            }
            return (
              <div key={listId} className={css.container}>
                {pendingReview && <div className={css.pending}>Pending Review</div>}
                {readyToShare && <div className={css.readytoshare}>Ready To Share</div>}
                {declined && <div className={css.declined}>Declined ({feedback})</div>}
                {shared && <div className={css.shared}>Shared</div>}
                <InfluencerCollectionListCard
                  key={listId}
                  listId={listId}
                  items={hearts}
                  name={name}
                  description={subCopy}
                  showShare={readyToShare || shared}
                  onOpenShareModal={onOpenShareModal}
                  onOpenDeleteModal={onOpenDeleteModal}
                />
              </div>
            );
          }
        })}
      </section>
      {isEmptyInfluencerCollection && (
        <section className={css.emptyCollection}>
          <div className={css.emptyCollectionLabel}>
            <p>Create your first Collection</p>
            <button type="button" className={css.addNew} onClick={onStartNewCollection} data-test-id={testId('addNewCollection')} />
          </div>
        </section>
      )}
      {!!collectionNextPageToken && (
        <button type="button" className={css.loadMore} ref={loadMore}>
          More
        </button>
      )}
    </div>
  );
};
